/**
 * platform-landing-page.tsx — Platform → Trang giới thiệu (super-admin). Route `/platform/landing`.
 *
 * Trang công khai ở tên miền ecdp.vn (và `/gioi-thieu` trên mọi tên miền app). Mặc định dùng file `public/landing/index.html` đi kèm mã nguồn;
 * ở đây có thể: đặt video cho nút "Khám phá eCDP", thay bằng bản HTML tải lên, hoặc khôi phục bản mặc định.
 * Backend: `platform/landing-page-routes.ts`. Logic hiển thị: `lib/landing.ts`.
 */
import { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { ExternalLink, FileUp, Loader2, RotateCcw, Save } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { platformApiError } from '@/lib/platform-client'
import { landingFrameSrc } from '@/lib/landing'
import { usePlatformLanding, useSaveLanding } from '@/hooks/use-platform-landing'

const MAX_MB = 3

export function PlatformLandingPage() {
  const { data, isLoading, isError } = usePlatformLanding()
  const save = useSaveLanding()
  const [video, setVideo] = useState('')
  const [html, setHtml] = useState('')
  const [fileName, setFileName] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (data) setVideo(data.videoUrl) }, [data])

  if (isLoading) return <Loading label="Đang tải trang giới thiệu…" />
  if (isError || !data) return <ErrorState message="Không tải được cấu hình trang giới thiệu" />

  const run = (input: { html?: string | null; videoUrl?: string }, ok: string) =>
    save.mutate(input, { onSuccess: () => toast.success(ok), onError: (e) => toast.error(platformApiError(e)) })

  const pickFile = async (f: File | undefined) => {
    if (!f) return
    if (f.size > MAX_MB * 1_000_000) { toast.error(`Tệp tối đa ${MAX_MB}MB`); return }
    setHtml(await f.text())
    setFileName(f.name)
  }

  const previewSrc = data.custom ? undefined : landingFrameSrc({ custom: false, videoUrl: data.videoUrl }, window.location.origin)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Trang giới thiệu"
        description="Trang công khai ở tên miền ecdp.vn (và đường dẫn /gioi-thieu). Mặc định dùng bản đi kèm mã nguồn; có thể thay bằng bản HTML của bạn."
        actions={
          <Button variant="outline" asChild>
            <a href="/gioi-thieu" target="_blank" rel="noopener noreferrer"><ExternalLink /> Mở trang công khai</a>
          </Button>
        }
      />

      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3 space-y-0">
          <div className="space-y-1">
            <CardTitle className="text-base">Đang hiển thị</CardTitle>
            <CardDescription>
              {data.custom ? 'Bản HTML tải lên' : 'Bản mặc định (frontend/public/landing/index.html)'}
              {data.updatedAt && <> · cập nhật {dayjs(data.updatedAt).format('HH:mm DD/MM/YYYY')}</>}
            </CardDescription>
          </div>
          <Badge variant={data.custom ? 'warning' : 'success'}>{data.custom ? 'Bản tải lên' : 'Bản mặc định'}</Badge>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border bg-muted/30">
            <iframe
              title="Xem trước trang giới thiệu"
              className="h-[560px] w-full border-0 bg-white"
              {...(data.custom
                ? { srcDoc: data.html, sandbox: 'allow-scripts allow-popups allow-popups-to-escape-sandbox' }
                : { src: previewSrc })}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Video giới thiệu</CardTitle>
          <CardDescription>Mở khi bấm nút “Khám phá eCDP”. Dùng địa chỉ https://… hoặc đường dẫn trên cùng máy chủ (vd /landing/video-gioi-thieu.html). Để trống: hiện thông báo “Video đang được cập nhật”.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1 space-y-1.5">
            <Label htmlFor="landing-video">Đường dẫn video</Label>
            <Input id="landing-video" value={video} onChange={(e) => setVideo(e.target.value)} placeholder="https://… hoặc /landing/video-gioi-thieu.html" />
          </div>
          <Button onClick={() => run({ videoUrl: video }, video.trim() ? 'Đã lưu video giới thiệu' : 'Đã bỏ video giới thiệu')} disabled={save.isPending || video === data.videoUrl}>
            {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Lưu
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Thay bằng bản HTML của bạn</CardTitle>
          <CardDescription>
            Tải lên một trang HTML tự chứa (tối đa {MAX_MB}MB). Ảnh dùng địa chỉ đầy đủ https://… hoặc nhúng base64. Bản tải lên chạy trong khung cách ly,
            không đọc được dữ liệu đăng nhập. Nút đăng nhập nên trỏ tới https://app.ecdp.vn/login với target="_top".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept=".html,text/html" className="hidden" onChange={(e) => { void pickFile(e.target.files?.[0]); e.target.value = '' }} />
            <Button variant="outline" onClick={() => fileRef.current?.click()}><FileUp /> Chọn tệp .html</Button>
            {fileName && <span className="text-sm text-muted-foreground">{fileName} · {(html.length / 1000).toFixed(0)}KB</span>}
          </div>
          <Textarea value={html} onChange={(e) => { setHtml(e.target.value); setFileName('') }} placeholder="…hoặc dán mã HTML vào đây" className="min-h-[140px] font-mono text-xs" />
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => run({ html }, 'Đã thay trang giới thiệu bằng bản tải lên')} disabled={save.isPending || !html.trim()}>
              {save.isPending ? <Loader2 className="animate-spin" /> : <Save />} Lưu bản tải lên
            </Button>
            {data.custom && (
              <Button
                variant="outline"
                onClick={() => { if (window.confirm('Bỏ bản tải lên và quay về bản mặc định?')) run({ html: null }, 'Đã khôi phục bản mặc định') }}
                disabled={save.isPending}
              >
                <RotateCcw /> Khôi phục bản mặc định
              </Button>
            )}
            <Button variant="ghost" asChild>
              <a href="/landing/index.html" target="_blank" rel="noopener noreferrer"><ExternalLink /> Xem mã bản mặc định</a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
